import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Section, Eyebrow, H1, Lead, FadeIn } from "../components/Primitives";
import { ShieldCheck, Sparkles } from "lucide-react";

export default function LoginPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "/try";

  // If already logged in, link to next.
  if (user) {
    return (
      <Section className="pt-24 pb-32" testId="login-already">
        <H1 className="max-w-2xl">You&apos;re already signed in.</H1>
        <p className="mt-4 text-sm text-zinc-600">Hi {user.name}, you&apos;re ready to go.</p>
        <Link to={next}><Button className="mt-6 rounded-full bg-zinc-950 text-white hover:bg-zinc-800">Continue →</Button></Link>
      </Section>
    );
  }

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback?next=" + encodeURIComponent(next);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="page-login">
      <Section className="pt-24 pb-32">
        <FadeIn><Eyebrow>Sign in</Eyebrow></FadeIn>
        <FadeIn delay={0.05}>
          <H1 className="mt-6 max-w-2xl">One-click access to your digital registers.</H1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <Lead className="mt-6">Sign in with Google to start digitizing your registers. The whole app is free — no plans, no payments, no limits.</Lead>
        </FadeIn>

        <div className="mt-10 max-w-md rounded-3xl border border-zinc-200 bg-white p-7">
          <button
            type="button"
            data-testid="login-google"
            onClick={startGoogle}
            className="flex w-full items-center justify-center gap-3 rounded-full border border-zinc-300 bg-white px-6 py-3 text-sm font-medium text-zinc-900 transition hover:border-zinc-950 hover:bg-zinc-50"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <ul className="mt-6 space-y-2.5 text-sm text-zinc-700">
            <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 text-zinc-950" /> Secure OAuth — we never see your password</li>
            <li className="flex items-start gap-2"><Sparkles className="mt-0.5 h-4 w-4 text-zinc-950" /> Unlimited photos, every export, all free</li>
            <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 text-zinc-950" /> Free forever · No card required</li>
          </ul>
          <p className="mt-5 text-[11px] text-zinc-500">
            By continuing you agree to our <Link to="/terms" className="underline">Terms</Link> and{" "}
            <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </Section>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.6 5.5 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.4-.1-2.6-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.6 5.5 29.5 3 24 3 16.1 3 9.3 7.5 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5.4 0 10.4-2 14.1-5.4l-6.5-5.5c-2 1.4-4.5 2.3-7.6 2.3-5.2 0-9.6-3.4-11.2-8.1l-6.5 5C9.3 40.5 16.1 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.5 5.5C40.4 36.9 45 31 45 24c0-1.4-.1-2.6-.4-3.5z"/>
    </svg>
  );
}
